import React, { useMemo } from "react";

import {
  ComparisonFlamebearer,
  ComparisonFlamebearerNode,
  createFormatterForProfileType,
  getProfileTypeTitle,
  ProfileType
} from "@platformsh/observability-graph/ContinuousProfiling";
import { PercentFormatter } from "@platformsh/observability-graph/Formatter";
import { useIntl } from "react-intl";

import ComparisonFlamebearerCallTooltipColorScale from "./ComparisonFlamebearerCallTooltipColorScale";
import * as S from "./FlamebearerCallTooltip.styles";

type Props = {
  node: ComparisonFlamebearerNode;
  flamebearer: ComparisonFlamebearer;
  profileType: ProfileType;
};

const percentFormatter = new PercentFormatter();

const ComparisonFlamebearerCallTooltip = ({
  node,
  flamebearer,
  profileType
}: Props) => {
  const intl = useIntl();
  const formatter = useMemo(
    () => createFormatterForProfileType(profileType),
    [profileType]
  );

  const sentence = useMemo(() => {
    if (!node.compared || (node.compared.inclusive.value ?? 0) === 0) {
      return intl.formatMessage({
        id: "conprof.flamebearer.comparison.tooltip.onlyB"
      });
    }
    if (node.inclusive.value === 0) {
      return intl.formatMessage({
        id: "conprof.flamebearer.comparison.tooltip.onlyA"
      });
    }
    const profileTypeTitle = getProfileTypeTitle(profileType.name);
    if ((node.compared.inclusive.value ?? 0) > node.inclusive.value) {
      return intl.formatMessage(
        {
          id: "conprof.flamebearer.comparison.tooltip.less"
        },
        {
          profileTypeTitle
        }
      );
    } else if ((node.compared.inclusive.value ?? 0) < node.inclusive.value) {
      return intl.formatMessage(
        {
          id: "conprof.flamebearer.comparison.tooltip.more"
        },
        {
          profileTypeTitle
        }
      );
    }
    return intl.formatMessage(
      {
        id: "conprof.flamebearer.comparison.tooltip.same"
      },
      {
        profileTypeTitle
      }
    );
  }, [intl, node.compared, node.inclusive.value, profileType.name]);

  const components = useMemo(
    () => Object.entries(node.call.components),
    [node.call.components]
  );

  return (
    <S.Layout>
      <S.Label>{node.call.label}</S.Label>

      {components.length > 0 && (
        <div>
          {components.map(([key, value]) => (
            <S.Component key={key}>
              <S.ComponentKey>
                {key}
                {": "}
              </S.ComponentKey>
              <S.ComponentValue>{value}</S.ComponentValue>
            </S.Component>
          ))}
        </div>
      )}

      <S.Text>{sentence}</S.Text>

      {flamebearer.deltaPeak !== undefined && (
        <ComparisonFlamebearerCallTooltipColorScale
          valueA={node.compared?.inclusive.value ?? 0}
          valueB={node.inclusive.value}
          diffPeak={flamebearer.deltaPeak}
          formatter={formatter}
        />
      )}

      <S.Comparison>
        <S.ComparisonSide>
          {node.compared && (
            <>
              <S.ComparisonSideLabel>{"A"}</S.ComparisonSideLabel>
              <div>
                {formatter.toCondensedString(
                  node.compared.inclusive.value ?? 0
                )}
              </div>
              <div>
                {percentFormatter.toStandardString(
                  node.compared.inclusive.impact
                )}
              </div>
            </>
          )}
        </S.ComparisonSide>
        <S.ComparisonSide>
          <S.ComparisonSideLabel>{"B"}</S.ComparisonSideLabel>
          <div>{formatter.toCondensedString(node.inclusive.value)}</div>
          <div>{percentFormatter.toStandardString(node.inclusive.impact)}</div>
        </S.ComparisonSide>
      </S.Comparison>

      <S.Footer>
        {intl.formatMessage({
          id: "conprof.flamebearer.comparison.tooltip.footer"
        })}
      </S.Footer>
    </S.Layout>
  );
};

export default ComparisonFlamebearerCallTooltip;
